import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Sparkles, ArrowLeft, Check, Loader2, Download, FileText, Mail, ExternalLink, Copy, Plus, AlertTriangle, AlertCircle, Info, ShieldAlert, RefreshCw, Palette, Wand2, FlaskConical, History as HistoryIcon } from "lucide-react";
import WhatIfDialog from "../components/WhatIfDialog";
import PoliceRosterView from "../components/PoliceRosterView";
import { fmtTime } from "../lib/dates";
import RosterLayoutPicker from "../components/RosterLayoutPicker";
import { TimelineRosterView, HeatmapRosterView, DayCardsRosterView } from "../components/RosterLayouts";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { AddEntryDialog } from "./roster-detail/AddEntryDialog";
import { BulkAddDialog } from "./roster-detail/BulkAddDialog";
import { HistoryDialog } from "./roster-detail/HistoryDialog";
import HealthScoreCard from "./roster-detail/HealthScoreCard";
import ActiveRulesPanel from "./roster-detail/ActiveRulesPanel";
import AIInstructionsPanel from "./roster-detail/AIInstructionsPanel";
import OperationsLegend from "./roster-detail/OperationsLegend";
import RosterHeader from "./roster-detail/RosterHeader";
import RosterStatsTiles from "./roster-detail/RosterStatsTiles";
import RosterAssignmentsTable from "./roster-detail/RosterAssignmentsTable";
import RosterViewToolbar from "./roster-detail/RosterViewToolbar";
import RosterGrid from "./roster-detail/RosterGrid";
import { useRosterGrid } from "../hooks/useRosterGrid";

const RosterDetail = () => {
  const { rosterId } = useParams();
  const navigate = useNavigate();
  const [roster, setRoster] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [violations, setViolations] = useState([]);
  const [generationInfo, setGenerationInfo] = useState(null); // {success, issues, warnings, can_override}
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [bulkAddDialog, setBulkAddDialog] = useState(false);
  const [bulkAddForm, setBulkAddForm] = useState({ shift_id: "", date: null, employee_id: "" });
  const [editColorShift, setEditColorShift] = useState(null);
  const [fixSuggestions, setFixSuggestions] = useState([]); // [{violation_index, violation, suggestion}]
  const [loadingFixes, setLoadingFixes] = useState(false);
  const [applyingFixIdx, setApplyingFixIdx] = useState(null);
  const [aiInstructions, setAiInstructions] = useState("");
  const [activeRules, setActiveRules] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [timeOff, setTimeOff] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const [operations, setOperations] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [rosterRes, empRes, shiftRes, rulesRes, toRes, opsRes, ltRes] = await Promise.all([
        axios.get(`${API}/rosters/${rosterId}`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/shifts`, { withCredentials: true }),
        axios.get(`${API}/rules`, { withCredentials: true }),
        axios.get(`${API}/time-off`, { withCredentials: true }),
        axios.get(`${API}/operations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/leave-types`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setRoster(rosterRes.data);
      setEmployees(empRes.data);
      setShifts(shiftRes.data);
      setActiveRules((rulesRes.data || []).filter(r => r.enabled));
      setTimeOff(toRes.data || []);
      setOperations(opsRes.data || []);
      setLeaveTypes(ltRes.data?.leave_types || null);
      setAiInstructions(rosterRes.data.ai_instructions || "");
      
      // Fetch notifications if published
      if (rosterRes.data.status === "published") {
        try {
          const notifRes = await axios.get(`${API}/rosters/${rosterId}/notifications`, { withCredentials: true });
          setNotifications(notifRes.data);
        } catch (e) {
          console.warn("Notifications unavailable:", e?.response?.status || e);
        }
      }
      
      // Validate roster for conflicts
      if (rosterRes.data.entries?.length > 0) {
        try {
          const valRes = await axios.get(`${API}/rosters/${rosterId}/validate`, { withCredentials: true });
          setViolations(valRes.data.violations || []);
        } catch (e) {
          console.warn("Validation unavailable:", e?.response?.status || e);
        }
        // Fetch health score
        try {
          const healthRes = await axios.get(`${API}/rosters/${rosterId}/health`, { withCredentials: true });
          setHealthScore(healthRes.data);
        } catch (e) {
          console.warn("Health score unavailable:", e?.response?.status || e);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load roster");
      navigate("/rosters");
    } finally {
      setLoading(false);
    }
  }, [rosterId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerate = async (overrideRules = false, regenerate = false) => {
    if (employees.length === 0) {
      toast.error("Please add employees first");
      return;
    }
    if (shifts.length === 0) {
      toast.error("Please add shift types first");
      return;
    }

    setGenerating(true);
    setGenerationInfo(null);
    try {
      const url = overrideRules 
        ? `${API}/rosters/${rosterId}/generate?override_rules=true`
        : `${API}/rosters/${rosterId}/generate`;
      const response = await axios.post(url, {
        ai_instructions: aiInstructions,
        regenerate: !!regenerate,
      }, { withCredentials: true });
      const data = response.data;
      
      if (data.success === false) {
        // Generation failed or has pre-flight issues
        setGenerationInfo({
          success: false,
          error_type: data.error_type,
          issues: data.issues || [],
          can_override: data.can_override || false
        });
        if (data.error_type === "preflight") {
          toast.error("Cannot generate roster — check the info panel below");
        } else if (data.error_type === "rule_conflict") {
          toast.error("Rule conflicts detected — review and override if needed");
        } else {
          toast.error("Roster generation failed — see details below");
        }
      } else {
        // Success
        const updatedRoster = data.roster || data;
        setRoster(updatedRoster);
        setGenerationInfo({
          success: true,
          warnings: data.warnings || [],
          override_used: data.override_used || false
        });
        
        if (data.warnings?.length > 0) {
          toast.success(`Roster generated with ${data.warnings.length} warning(s)`);
        } else {
          toast.success("Roster generated successfully!");
        }
        revalidate();
      }
    } catch (error) {
      console.error("Failed to generate roster:", error);
      const detail = error.response?.data?.detail || error.message;
      setGenerationInfo({
        success: false,
        error_type: "network",
        issues: [{
          type: "request_failed",
          severity: "error",
          title: "Request failed",
          message: typeof detail === "string" ? detail : "Network error or server unavailable.",
          action: "Check your connection and try again."
        }],
        can_override: false
      });
      toast.error("Failed to generate roster");
    } finally {
      setGenerating(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/rosters/${rosterId}/history`, { withCredentials: true });
      setHistory(res.data?.versions || []);
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    fetchHistory();
  };

  const handleRevert = async (versionId) => {
    if (!window.confirm("Revert the roster to this earlier version? Your current entries will be saved as a new history snapshot.")) return;
    setRevertingId(versionId);
    try {
      const res = await axios.post(`${API}/rosters/${rosterId}/revert/${versionId}`, {}, { withCredentials: true });
      setRoster(res.data?.roster || roster);
      toast.success("Reverted to previous version");
      setHistoryOpen(false);
      revalidate();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally {
      setRevertingId(null);
    }
  };

  const revalidate = async () => {
    try {
      const valRes = await axios.get(`${API}/rosters/${rosterId}/validate`, { withCredentials: true });
      setViolations(valRes.data.violations || []);
      // Clear suggestions when re-validating — they may be stale
      setFixSuggestions([]);
    } catch (e) {
      console.warn("Re-validation failed:", e?.response?.status || e);
    }
  };

  const {
    calendarView, setCalendarView,
    weekOffset, setWeekOffset,
    layoutPickerOpen, setLayoutPickerOpen,
    dayCardDate, setDayCardDate,
    currentLayout,
    dates, visibleDates, maxOffset, getViewLabel,
    dragData, handleDragStart, handleDragOver, handleDrop,
    getEntriesForDate, getEmployeeName, getShift, getInitials,
    leaveBadge, getLeaveForCell, operationForSlot, countLeaveDaysForEmployee,
    handleDeleteEntry, handleAddEntry,
    addEntryDialog, setAddEntryDialog,
  } = useRosterGrid({ roster, setRoster, rosterId, employees, shifts, timeOff, leaveTypes, operations, revalidate });

  const fetchFixSuggestions = async () => {
    setLoadingFixes(true);
    try {
      const res = await axios.get(`${API}/rosters/${rosterId}/suggest-fixes`, { withCredentials: true });
      setFixSuggestions(res.data.suggestions || []);
      const withFix = (res.data.suggestions || []).filter(s => s.suggestion).length;
      if (withFix === 0) {
        toast.info("No automatic fixes available for current violations");
      } else {
        toast.success(`Found ${withFix} suggested ${withFix === 1 ? "fix" : "fixes"}`);
      }
    } catch (e) {
      toast.error("Failed to load fix suggestions");
    } finally {
      setLoadingFixes(false);
    }
  };

  const applyFix = async (suggestionIdx) => {
    const item = fixSuggestions[suggestionIdx];
    if (!item?.suggestion?.action) return;
    setApplyingFixIdx(suggestionIdx);
    try {
      await axios.post(`${API}/rosters/${rosterId}/apply-fix`, item.suggestion.action, { withCredentials: true });
      toast.success("Fix applied");
      await fetchData();
      await revalidate();
      // Auto-refresh suggestions so next violation shows the next fix
      setTimeout(() => { fetchFixSuggestions(); }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply fix");
    } finally {
      setApplyingFixIdx(null);
    }
  };

  const handleUpdateShiftColor = async (shiftId, newColor, newDescription) => {
    const shift = shifts.find(s => s.shift_id === shiftId);
    if (!shift) return;
    try {
      await axios.put(`${API}/shifts/${shiftId}`, {
        name: shift.name,
        start_time: shift.start_time,
        end_time: shift.end_time,
        color: newColor,
        description: newDescription ?? shift.description ?? "",
        is_standard: shift.is_standard
      }, { withCredentials: true });
      // Update local state
      setShifts(prev => prev.map(s =>
        s.shift_id === shiftId ? { ...s, color: newColor, description: newDescription ?? s.description } : s
      ));
      toast.success("Shift color updated");
    } catch (error) {
      toast.error("Failed to update color");
    }
  };

  const colorOptions = [
    { value: "#002FA7", label: "Blue" },
    { value: "#00C781", label: "Green" },
    { value: "#FFCC00", label: "Yellow" },
    { value: "#FF3B30", label: "Red" },
    { value: "#8B5CF6", label: "Purple" },
    { value: "#F97316", label: "Orange" },
    { value: "#52525B", label: "Gray" },
    { value: "#06B6D4", label: "Cyan" },
    { value: "#EC4899", label: "Pink" },
    { value: "#84CC16", label: "Lime" },
  ];

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await axios.put(`${API}/rosters/${rosterId}/publish`, {}, { withCredentials: true });
      setRoster(response.data);
      toast.success("Roster published! Sending email notifications…");
      // After a short delay, poll the summary endpoint and surface the real send/skipped/failed result
      setTimeout(async () => {
        try {
          const summary = await axios.get(`${API}/rosters/${rosterId}/notification-summary`, { withCredentials: true });
          const s = summary.data || {};
          const sent = s.sent || 0;
          const failed = s.failed || 0;
          const skipped = s.skipped || 0;
          if (failed > 0 && s.sample_error) {
            toast.error(
              `Email delivery problem (${failed} failed). Reason: ${s.sample_error.slice(0, 200)}`,
              { duration: 12000, id: `pub-${rosterId}` }
            );
          } else if (sent === 0 && skipped > 0) {
            toast.warning(
              `No emails sent — all ${skipped} employees had invalid or missing email addresses. Add valid emails to your employees and re-publish.`,
              { duration: 10000, id: `pub-${rosterId}` }
            );
          } else {
            toast.success(
              `${sent} email${sent === 1 ? "" : "s"} sent${skipped ? `, ${skipped} skipped (invalid emails)` : ""}.`,
              { duration: 6000, id: `pub-${rosterId}` }
            );
          }
          const notifRes = await axios.get(`${API}/rosters/${rosterId}/notifications`, { withCredentials: true });
          setNotifications(notifRes.data);
        } catch (e) { console.warn("Notifications fetch failed:", e?.response?.status || e); }
      }, 3500);
    } catch (error) {
      console.error("Failed to publish roster:", error);
      toast.error("Failed to publish roster");
    } finally {
      setPublishing(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting("csv");
    try {
      const response = await axios.get(`${API}/rosters/${rosterId}/export/csv`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `roster_${roster.name.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (error) {
      console.error("Export CSV failed:", error);
      toast.error("Failed to export CSV");
    } finally {
      setExporting(null);
    }
  };

  const handleExportPDF = async () => {
    setExporting("pdf");
    try {
      const response = await axios.get(`${API}/rosters/${rosterId}/export/pdf`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `roster_${roster.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("Export PDF failed:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(null);
    }
  };

  const handleSaveAsTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/templates/from-roster/${rosterId}`, {}, { withCredentials: true });
      toast.success("Saved as template! Find it under Templates.");
    } catch (error) {
      console.error("Failed to save template:", error);
      toast.error("Failed to save as template");
    } finally {
      setSavingTemplate(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!roster) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-zinc-500 dark:text-zinc-400">Roster not found</p>
        </div>
      </Layout>
    );
  }


  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <RosterHeader
          roster={roster}
          generating={generating}
          publishing={publishing}
          onBack={() => navigate("/rosters")}
          onWhatIf={() => setWhatIfOpen(true)}
          onPublish={handlePublish}
          onGenerate={handleGenerate}
          onRegenerateKeepLocks={() => handleGenerate(false, true)}
          onOpenHistory={handleOpenHistory}
        />

        {/* Info Cards */}
        <RosterStatsTiles
          employeesCount={employees.length}
          shiftsCount={shifts.length}
          assignmentsCount={roster.entries?.length || 0}
        />

        {/* Roster Health Score */}
        <HealthScoreCard healthScore={healthScore} hasEntries={(roster.entries?.length || 0) > 0} />

        {/* Active Rules Summary */}
        <ActiveRulesPanel activeRules={activeRules} onManageClick={() => navigate("/rules")} />

        {/* Operations Legend */}
        <OperationsLegend
          operations={operations}
          rosterStart={roster.start_date}
          rosterEnd={roster.end_date}
          onManageClick={() => navigate("/operations")}
        />

        {/* AI Instructions */}
        <AIInstructionsPanel
          aiInstructions={aiInstructions}
          setAiInstructions={setAiInstructions}
          isPublished={roster.status === 'published'}
        />

        {/* Color Legend */}
        {shifts.length > 0 && roster.entries?.length > 0 && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="color-legend">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-[#002FA7]" />
                <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Color Legend</h3>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">Click a color to change it</p>
            </div>
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {shifts.map((shift) => (
                <div key={shift.shift_id} className="p-3 flex items-center gap-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors" data-testid={`legend-${shift.shift_id}`}>
                  {/* Color swatch — clickable to open color picker */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="w-8 h-8 rounded border-2 border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 transition-colors flex-shrink-0 cursor-pointer"
                        style={{ backgroundColor: shift.color }}
                        data-testid={`legend-color-${shift.shift_id}`}
                      />
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="start">
                      <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-2">Change color for {shift.name}</p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {colorOptions.map((c) => (
                          <button
                            key={c.value}
                            onClick={() => handleUpdateShiftColor(shift.shift_id, c.value)}
                            className={`w-7 h-7 rounded border-2 transition-all ${
                              shift.color === c.value ? 'border-zinc-900 scale-110' : 'border-transparent hover:scale-105'
                            }`}
                            style={{ backgroundColor: c.value }}
                            title={c.label}
                            data-testid={`pick-color-${shift.shift_id}-${c.label.toLowerCase()}`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-zinc-500 dark:text-zinc-400">Custom:</label>
                        <input
                          type="color"
                          value={shift.color}
                          onChange={(e) => handleUpdateShiftColor(shift.shift_id, e.target.value)}
                          className="w-8 h-6 cursor-pointer border border-zinc-200 dark:border-zinc-800 rounded"
                          data-testid={`custom-color-${shift.shift_id}`}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Shift info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{shift.name}</p>
                      <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">{fmtTime(shift.start_time)} — {fmtTime(shift.end_time)}</span>
                    </div>
                    {shift.description ? (
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">{shift.description}</p>
                    ) : (
                      <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5 italic">No description — add one in Shift Types</p>
                    )}
                  </div>

                  {/* Entry count for this shift */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">
                      {roster.entries.filter(e => e.shift_id === shift.shift_id).length}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">entries</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Export & Portal Actions */}
        {roster.entries?.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={exporting === "csv"}
              data-testid="export-csv-btn"
            >
              {exporting === "csv" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={exporting === "pdf"}
              data-testid="export-pdf-btn"
            >
              {exporting === "pdf" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${window.location.origin}/portal`, '_blank')}
              data-testid="open-portal-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Employee Portal
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate}
              data-testid="save-template-btn"
            >
              {savingTemplate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Save as Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkAddDialog(true)}
              data-testid="bulk-add-shift-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Shift to Date
            </Button>
          </div>
        )}

        {/* Conflict Detection Panel */}
        {violations.length > 0 && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="violations-panel">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Rule Violations ({violations.length})
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {violations.filter(v => v.severity === "error").length > 0 && (
                  <span className="text-xs font-medium px-2 py-0.5 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                    {violations.filter(v => v.severity === "error").length} errors
                  </span>
                )}
                {violations.filter(v => v.severity === "warning").length > 0 && (
                  <span className="text-xs font-medium px-2 py-0.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                    {violations.filter(v => v.severity === "warning").length} warnings
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchFixSuggestions}
                  disabled={loadingFixes || roster?.status === 'published'}
                  className="ml-2 h-7 text-xs"
                  data-testid="suggest-fixes-btn"
                >
                  {loadingFixes ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                  ) : (
                    <Wand2 className="w-3 h-3 mr-1.5" />
                  )}
                  Suggest Fixes
                </Button>
              </div>
            </div>
            <div className="divide-y divide-zinc-100 dark:divide-zinc-800 max-h-[360px] overflow-y-auto">
              {violations.map((v, idx) => {
                const sugg = fixSuggestions.find(s => s.violation_index === idx);
                const suggIdx = fixSuggestions.findIndex(s => s.violation_index === idx);
                return (
                  <div key={idx} className="px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors" data-testid={`violation-row-${idx}`}>
                    <div className="flex items-start gap-3">
                      {v.severity === "error" ? (
                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-zinc-900 dark:text-zinc-100">{v.message}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">{v.rule_name}</p>
                        {sugg?.suggestion && (
                          <div className="mt-2 flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded" data-testid={`fix-suggestion-${idx}`}>
                            <Wand2 className="w-3.5 h-3.5 text-[#002FA7] dark:text-blue-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-zinc-700 dark:text-zinc-300">{sugg.suggestion.text}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => applyFix(suggIdx)}
                              disabled={applyingFixIdx !== null || roster?.status === 'published'}
                              className="h-6 px-2 text-[11px] bg-[#002FA7] hover:bg-[#00227A] text-white flex-shrink-0"
                              data-testid={`apply-fix-${idx}`}
                            >
                              {applyingFixIdx === suggIdx ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                "Apply"
                              )}
                            </Button>
                          </div>
                        )}
                        {sugg && !sugg.suggestion && (
                          <p className="mt-1.5 text-[11px] text-zinc-400 dark:text-zinc-500 italic">No automatic fix available — adjust manually.</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notification Status */}
        {notifications.length > 0 && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-[#002FA7]" />
              <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Email Notifications</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`
                    text-xs px-2.5 py-1 rounded flex items-center gap-1.5
                    ${notif.status === 'sent' 
                      ? 'bg-emerald-50 text-emerald-700' 
                      : 'bg-red-50 text-red-700'}
                  `}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${notif.status === 'sent' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  {notif.email} — {notif.status}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Roster Calendar */}

        {/* Generation Info Box */}
        {generationInfo && !generating && (
          <div className={`border p-5 ${
            generationInfo.success 
              ? generationInfo.warnings?.length > 0 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-emerald-50 border-emerald-200'
              : 'bg-red-50 border-red-200'
          }`} data-testid="generation-info-box">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0">
                {generationInfo.success ? (
                  generationInfo.warnings?.length > 0 ? (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  ) : (
                    <Check className="w-5 h-5 text-emerald-600" />
                  )
                ) : (
                  <ShieldAlert className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div className="flex-1">
                <h3 className={`text-sm font-medium ${
                  generationInfo.success 
                    ? generationInfo.warnings?.length > 0 ? 'text-amber-800' : 'text-emerald-800'
                    : 'text-red-800'
                }`}>
                  {generationInfo.success 
                    ? generationInfo.override_used 
                      ? 'Roster generated with rule overrides'
                      : generationInfo.warnings?.length > 0 
                        ? 'Roster generated with warnings'
                        : 'Roster generated successfully'
                    : generationInfo.error_type === 'preflight' 
                      ? 'Cannot generate roster'
                      : generationInfo.error_type === 'rule_conflict'
                        ? 'Rule conflicts detected'
                        : 'Roster generation failed'
                  }
                </h3>

                {/* Show issues */}
                {(generationInfo.issues || generationInfo.warnings || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {(generationInfo.success ? generationInfo.warnings : generationInfo.issues).map((issue, idx) => (
                      <div key={idx} className={`p-3 rounded border ${
                        issue.severity === 'error' ? 'bg-white dark:bg-zinc-900 border-red-200' :
                        issue.severity === 'warning' ? 'bg-white dark:bg-zinc-900 border-amber-200' :
                        'bg-white dark:bg-zinc-900 border-blue-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          {issue.severity === 'error' ? (
                            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          ) : issue.severity === 'warning' ? (
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{issue.title}</p>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">{issue.message}</p>
                            {issue.action && (
                              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 flex items-center gap-1">
                                <span className="font-medium">Suggestion:</span> {issue.action}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Override button */}
                {!generationInfo.success && generationInfo.can_override && (
                  <div className="mt-4 pt-3 border-t border-red-200 flex items-center gap-3">
                    <Button
                      onClick={() => handleGenerate(true)}
                      disabled={generating}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                      data-testid="override-generate-btn"
                    >
                      <ShieldAlert className="w-4 h-4 mr-2" />
                      Override Rules & Generate Anyway
                    </Button>
                    <p className="text-xs text-red-600">
                      The AI will try its best but some rules may be violated
                    </p>
                  </div>
                )}

                {/* Retry button for errors */}
                {!generationInfo.success && !generationInfo.can_override && generationInfo.error_type !== 'preflight' && (
                  <div className="mt-4 pt-3 border-t border-red-200">
                    <Button
                      onClick={() => handleGenerate(false)}
                      disabled={generating}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-100"
                      data-testid="retry-generate-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                )}

                {/* Dismiss button for success */}
                {generationInfo.success && (
                  <div className="mt-3">
                    <button
                      onClick={() => setGenerationInfo(null)}
                      className="text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:text-zinc-300 underline"
                      data-testid="dismiss-info-btn"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Roster Calendar - Generating State */}
        {generating && (
          <div className="bg-white dark:bg-zinc-900 border-2 border-[#002FA7] p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 tracing-beam"></div>
            <div className="flex items-center justify-center gap-4">
              <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin" />
              <div className="text-center">
                <p className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Generating optimal roster...</p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                  AI is analyzing rules, preferences, and constraints
                </p>
              </div>
            </div>
          </div>
        )}

        {!generating && roster.entries?.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">No roster generated yet</h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6 max-w-md mx-auto">
              Click "Generate with AI" to create an optimized schedule based on your employees, 
              shifts, rules, and time-off requests.
            </p>
            <Button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="generate-empty-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Roster
            </Button>
          </div>
        ) : !generating && (
          <div className="space-y-0">
            {/* View Toggle Bar */}
            <RosterViewToolbar
              calendarView={calendarView}
              currentLayout={currentLayout}
              onOpenLayoutPicker={() => setLayoutPickerOpen(true)}
              weekOffset={weekOffset}
              maxOffset={maxOffset}
              onPrev={() => setWeekOffset(Math.max(0, weekOffset - 1))}
              onNext={() => setWeekOffset(Math.min(maxOffset, weekOffset + 1))}
              viewLabel={getViewLabel()}
              dates={dates}
              shifts={shifts}
            />

            {/* Custom layouts swap out the standard grid */}
            {calendarView === "police" ? (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <PoliceRosterView
                  roster={roster}
                  employees={employees}
                  shifts={shifts}
                  timeOff={timeOff}
                  teamName={roster.name?.split(" ")[0]?.toUpperCase() || ""}
                />
              </div>
            ) : calendarView === "timeline" ? (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <TimelineRosterView roster={roster} employees={employees} shifts={shifts} />
              </div>
            ) : calendarView === "heatmap" ? (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <HeatmapRosterView roster={roster} employees={employees} shifts={shifts} />
              </div>
            ) : calendarView === "daycards" ? (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <DayCardsRosterView
                  roster={roster}
                  employees={employees}
                  shifts={shifts}
                  selectedDate={dayCardDate}
                  onSelectDate={setDayCardDate}
                />
              </div>
            ) : (
              <RosterGrid
                visibleDates={visibleDates}
                employees={employees}
                roster={roster}
                dragData={dragData}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDeleteEntry={handleDeleteEntry}
                onAddEntryClick={setAddEntryDialog}
                getEntriesForDate={getEntriesForDate}
                getShift={getShift}
                getInitials={getInitials}
                countLeaveDaysForEmployee={countLeaveDaysForEmployee}
                getLeaveForCell={getLeaveForCell}
                leaveBadge={leaveBadge}
                operationForSlot={operationForSlot}
              />
            )}
          </div>
        )}

        {/* Full Roster Table */}
        {!generating && roster.entries?.length > 0 && (
          <RosterAssignmentsTable
            entries={roster.entries}
            getShift={getShift}
            getEmployeeName={getEmployeeName}
          />
        )}
        
        {/* Add Entry Dialog */}
        <AddEntryDialog
          entry={addEntryDialog}
          shifts={shifts}
          getEmployeeName={getEmployeeName}
          onSelect={handleAddEntry}
          onClose={() => setAddEntryDialog(null)}
        />

        {/* Bulk Add Shift to Date Dialog */}
        <BulkAddDialog
          open={bulkAddDialog}
          form={bulkAddForm}
          onFormChange={setBulkAddForm}
          shifts={shifts}
          employees={employees}
          onClose={() => setBulkAddDialog(false)}
          onConfirm={async () => {
            await handleAddEntry(
              bulkAddForm.employee_id,
              format(bulkAddForm.date, "yyyy-MM-dd"),
              bulkAddForm.shift_id
            );
            setBulkAddDialog(false);
            setBulkAddForm({ shift_id: "", date: null, employee_id: "" });
          }}
        />

        <WhatIfDialog
          open={whatIfOpen}
          onOpenChange={setWhatIfOpen}
          rosterId={rosterId}
          onApplied={fetchData}
        />

        <RosterLayoutPicker
          open={layoutPickerOpen}
          onOpenChange={setLayoutPickerOpen}
          value={calendarView}
          onChange={(v) => { setCalendarView(v); setWeekOffset(0); }}
        />

        {/* Roster History / Revert Dialog */}
        <HistoryDialog
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          history={history}
          loading={loadingHistory}
          revertingId={revertingId}
          onRevert={handleRevert}
        />
      </div>
    </Layout>
  );
};

export default RosterDetail;
